import { api } from '@/lib/api';
import type { CompleteRefundTrackingRequest, CreateReceiptRequest } from '@valiosascrm/shared';
import type { PaymentMethod } from '@/services/cash-flow';

// Story 65.19 — recebimento em partes (Receipt V2) e devolução a acompanhar. As rotas só
// respondem na clínica em que a escrita está ligada; `getReceiptStatus` é a pergunta que a tela
// faz antes de mostrar qualquer coisa nova.

export interface ReceiptObligation {
  transactionId: string;
  description: string;
  amount: number;
  alreadyAllocated: number;
  balance: number;
  status: string;
  revision: number;
  settlementModel: string;
  serviceId: string | null;
  serviceName: string | null;
  appointmentId: string | null;
}

export interface ReceiptObligations {
  obligations: ReceiptObligation[];
  patient: { id: string; creditBalance: number } | null;
}

export interface ReceiptView {
  replay: boolean;
  receipt: {
    id: string;
    requestId: string;
    status: string;
    revision: number;
    patientId: string | null;
    partialReason: string | null;
    parts: Array<{
      id: string;
      clientPartId: string;
      sourceType: string;
      paymentMethod: PaymentMethod | null;
      receivedAmount: number | null;
      appliedAmount: number;
      changeAmount: number;
      excessAmount: number;
      feeState: string;
    }>;
  };
  obligations: Array<{ transactionId: string; status: string; revision: number; balance: number }>;
}

export interface RefundTracking {
  id: string;
  amount: number;
  status: 'pending' | 'completed' | 'cancelled';
  reason: string;
  revision: number;
  receiptPartId: string | null;
  legacyTransactionId: string | null;
  movementTransactionId: string | null;
  refundPaymentMethod: PaymentMethod | null;
  refundEffectivePaidAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

export async function getReceiptStatus(): Promise<{ enabled: boolean; readOnly?: boolean }> {
  return (await api.get<{ enabled: boolean; readOnly?: boolean }>('/cash-flow/receipts/status')).data;
}

export async function getReceiptObligations(transactionIds: string[]): Promise<ReceiptObligations> {
  return (await api.get<ReceiptObligations>('/cash-flow/receipts/obligations', { params: { transactionIds: transactionIds.join(',') } })).data;
}

export async function createReceipt(request: CreateReceiptRequest): Promise<ReceiptView> {
  return (await api.post<ReceiptView>('/cash-flow/receipts', request)).data;
}

export async function getRefundTrackings(status: RefundTracking['status'] = 'pending'): Promise<RefundTracking[]> {
  return (await api.get<RefundTracking[]>('/cash-flow/refund-trackings', { params: { status } })).data;
}

export async function completeRefundTracking(id: string, request: CompleteRefundTrackingRequest): Promise<{ replay: boolean; tracking: RefundTracking }> {
  return (await api.post<{ replay: boolean; tracking: RefundTracking }>(`/cash-flow/refund-trackings/${id}/complete`, request)).data;
}
